/**
 * Calendar Grid Component
 *
 * Supports Weekly and Monthly views
 * Shows event indicators only for dates with actual workouts
 */

import { type CalendarView } from '@/pages/Schedule';
import './CalendarGrid.css';

interface CalendarEvent {
  id: string;
  title: string;
  type: 'workout' | 'task' | 'appointment' | 'other';
  date: Date;
}

interface CalendarGridProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  events: CalendarEvent[];
  view: CalendarView;
}

function getWeekDays(date: Date): Date[] {
  const curr = new Date(date);
  const first = curr.getDate() - curr.getDay();

  const week: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(curr);
    day.setDate(first + i);
    week.push(day);
  }
  return week;
}

function getMonthDays(date: Date): Date[] {
  const year = date.getFullYear();
  const month = date.getMonth();

  // First day of the month
  const firstDay = new Date(year, month, 1);
  // Last day of the month
  const lastDay = new Date(year, month + 1, 0);

  // Start from Sunday of the week containing the first day
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - startDate.getDay());

  // End on Saturday of the week containing the last day
  const endDate = new Date(lastDay);
  endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

  const days: Date[] = [];
  const current = new Date(startDate);

  while (current <= endDate) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return days;
}

function navigateMonth(date: Date, direction: number): Date {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + direction);
  return newDate;
}

function navigateWeek(date: Date, direction: number): Date {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + direction * 7);
  return newDate;
}

export function CalendarGrid({ selectedDate, onDateSelect, events, view }: CalendarGridProps): JSX.Element {
  const today = new Date();
  // Single-letter day names for compact display
  const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const hasEvent = (date: Date): boolean => {
    return events.some((event) =>
      event.date.toDateString() === date.toDateString()
    );
  };

  const isToday = (date: Date): boolean => {
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date): boolean => {
    return date.toDateString() === selectedDate.toDateString();
  };

  const isCurrentMonth = (date: Date): boolean => {
    return date.getMonth() === selectedDate.getMonth();
  };

  const handleNavigate = (direction: number): void => {
    if (view === 'monthly') {
      onDateSelect(navigateMonth(selectedDate, direction));
    } else {
      onDateSelect(navigateWeek(selectedDate, direction));
    }
  };

  const days = view === 'monthly' ? getMonthDays(selectedDate) : getWeekDays(selectedDate);
  const firstDay = days[0];
  const lastDay = days[6];

  // Format header based on view
  const headerText = view === 'monthly'
    ? selectedDate.toLocaleDateString('en-US', { month: 'long' })
    : firstDay && lastDay
      ? `${firstDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${lastDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
      : '';

  const yearText = selectedDate.getFullYear().toString();

  return (
    <div className={`calendar-grid calendar-grid--${view}`}>
      {/* Navigation Header */}
      <div className="calendar-grid__nav">
        {view === 'monthly' && (
          <span className="calendar-grid__year">{yearText}</span>
        )}
        <div className="calendar-grid__nav-center">
          <button
            type="button"
            className="calendar-grid__nav-button"
            onClick={() => handleNavigate(-1)}
            aria-label={view === 'monthly' ? 'Previous month' : 'Previous week'}
          >
            ‹
          </button>
          <span className="calendar-grid__nav-title">{headerText}</span>
          <button
            type="button"
            className="calendar-grid__nav-button"
            onClick={() => handleNavigate(1)}
            aria-label={view === 'monthly' ? 'Next month' : 'Next week'}
          >
            ›
          </button>
        </div>
        {view === 'monthly' && <span className="calendar-grid__spacer" />}
      </div>

      {/* Day Names Header */}
      <div className="calendar-grid__header">
        {dayNames.map((name, index) => (
          <div key={`${name}-${index}`} className="calendar-grid__day-name">
            {name}
          </div>
        ))}
      </div>

      {/* Calendar Days */}
      <div className="calendar-grid__days">
        {days.map((date) => (
          <button
            key={date.toISOString()}
            type="button"
            className={`calendar-grid__day ${
              isToday(date) ? 'calendar-grid__day--today' : ''
            } ${
              isSelected(date) ? 'calendar-grid__day--selected' : ''
            } ${
              view === 'monthly' && !isCurrentMonth(date) ? 'calendar-grid__day--outside' : ''
            }`}
            onClick={() => onDateSelect(date)}
          >
            <span className="calendar-grid__day-number">{date.getDate()}</span>
            {hasEvent(date) && (
              <span className="calendar-grid__event-indicator" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
